"use client";

import React, { useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";

const EnqueryForm = dynamic(() => import("@/components/forms/EnqueryForm"), {
  ssr: false,
});

type CardProps = {
  title: string;
  amount: string;
  subtitleDesktop: string;
  subtitleMobileTop: string; // e.g., "Work at Big 4 &"
  subtitleMobileBottom: string; // e.g., "220+ MNCs"
  logoPanelDeskSrc: string;
  logoPanelDeskAlt: string;
  logoPanelDeskW: number;
  logoPanelDeskH: number;
  logoPanelMobSrc: string;
  logoPanelMobAlt: string;
  logoPanelMobW: number;
  logoPanelMobH: number;
};

function SalaryCard({
  title,
  amount,
  subtitleDesktop,
  subtitleMobileTop,
  subtitleMobileBottom,
  logoPanelDeskSrc,
  logoPanelDeskAlt,
  logoPanelDeskW,
  logoPanelDeskH,
  logoPanelMobSrc,
  logoPanelMobAlt,
  logoPanelMobW,
  logoPanelMobH,
}: CardProps) {
  return (
    <div className="w-full relative mx-auto">
      <div className="mx-auto">
        <div className="relative rounded-[12px] border-[1px] border-[rgba(248,248,248,0.3)] bg-[#3399FF] backdrop-blur-[8.537px]">
          {/* Header */}
          <div className="px-1 md:px-6 pt-2 md:pt-3 text-white text-center">
            <p className="text-xs md:text-lg font-semibold leading-4">
              {title}
            </p>
            <p className="text-[26px] md:text-[32px] font-bold tracking-tight leading-8">
              {amount}
            </p>
          </div>

          {/* Body */}
          <div className="m-2 md:m-3 rounded-[12px] bg-[#F8F8F8] border border-[#EDEDF0]">
            <div className="px-4 md:px-6 py-2 md:py-3">
              <p className="text-center text-black text-xs md:text-lg font-medium md:block hidden">
                {subtitleDesktop}
              </p>
              <p className="text-center md:text-black text-[rgba(0,15,52,0.6)] text-xs font-medium md:hidden block">
                {subtitleMobileTop} <br className="md:hidden" />
                {subtitleMobileBottom}
              </p>

              <div className="mt-4 md:mt-6 justify-center hidden md:flex">
                <Image
                  src={logoPanelDeskSrc}
                  alt={logoPanelDeskAlt}
                  width={logoPanelDeskW}
                  height={logoPanelDeskH}
                  className="h-12 w-auto object-contain"
                />
              </div>

              <div className="mt-3 justify-center flex md:hidden">
                <Image
                  src={logoPanelMobSrc}
                  alt={logoPanelMobAlt}
                  width={logoPanelMobW}
                  height={logoPanelMobH}
                  className="w-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Milestone = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);

  const handleFormModelOpen = () => {
    sessionStorage.setItem("modal-open", "true");
    setIsFormModalOpen(true);
  };

  const handleFormModelClose = () => {
    sessionStorage.removeItem("modal-open");
    setIsFormModalOpen(false);
  };

  useModalScrollLock(isFormModalOpen, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  const cardsData: CardProps[] = [
    {
      title: "Avg. Salary in India",
      amount: "₹20 lakhs+",

      subtitleDesktop: "Work at Big 4 & 220+ MNCs",
      subtitleMobileTop: "Work at Big 4 &",
      subtitleMobileBottom: "220+ MNCs",

      // Desktop logos
      logoPanelDeskSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/big4_20lakhs.webp",
      logoPanelDeskAlt: "Deloitte, PwC, KPMG, EY",
      logoPanelDeskW: 640,
      logoPanelDeskH: 120,

      logoPanelMobSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/big4_20lakhs_mob.webp",
      logoPanelMobAlt: "Deloitte, PwC, KPMG, EY (mobile)",
      logoPanelMobW: 360,
      logoPanelMobH: 80,
    },
    {
      title: "Avg. Salary in USA",
      amount: "₹50 lakhs+",

      subtitleDesktop: "Work at 350+ CPA firms",
      subtitleMobileTop: "Work at 350+",
      subtitleMobileBottom: "CPA firms",

      // mobile logos
      logoPanelDeskSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/big4_50lakhs.webp",
      logoPanelDeskAlt: "RSM, BDO Alliance USA, Allinial Global, CPAmerica",
      logoPanelDeskW: 640,
      logoPanelDeskH: 120,

      logoPanelMobSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/big4_50lakhs_mob.webp",
      logoPanelMobAlt:
        "RSM, BDO Alliance USA, Allinial Global, CPAmerica (mobile)",
      logoPanelMobW: 360,
      logoPanelMobH: 80,
    },
  ];
  return (
    <>
      <div className="w-full bg-white py-8 md:py-12">
        <div className="main_container mx-auto px-4 md:px-6">
          {/* Milestone 1 - CAIRA */}
          <div className="mb-8 md:mb-12">
            <div className="flex flex-row items-center gap-2 md:gap-4 mb-4 md:mb-6">
              <div>
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma/launch.webp?tr=w-48,h-48"
                  alt="Milestone Icon"
                  width={48}
                  height={48}
                  className="w-8 h-8 md:w-12 md:h-12 md:mb-1 mb-1"
                  loading="lazy"
                />
              </div>
              <div className="">
                <h2 className="text-[#3399FF] text-base md:text-2xl font-bold leading-[1] md:leading-[1]">
                  Milestone 1
                </h2>
                <h3 className="text-[#061A2F] text-sm md:text-xl font-semibold ">
                  CAIRA (Certified AI-Ready Accountant)
                </h3>
              </div>
            </div>

            {/* CAIRA Card - Desktop */}
            <div
              className="hidden md:block cursor-pointer"
              onClick={handleFormModelOpen}
            >
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma/poster-desk.webp"
                alt="CAIRA Certification - FREE for Miles CPA/CMA candidates"
                width={1280}
                height={300}
                className="w-full h-auto rounded-lg object-cover"
                loading="lazy"
              />
            </div>

            {/* CAIRA Card - Mobile */}
            <div
              className="md:hidden cursor-pointer"
              onClick={handleFormModelOpen}
            >
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma/poster-mob.webp?tr=w-380,h-206,q-80"
                alt="CAIRA Certification - FREE for Miles CPA/CMA candidates"
                width={380}
                height={206}
                sizes="380px"
                className="w-full h-auto rounded-2xl"
                loading="lazy"
              />
            </div>
          </div>

          {/* Milestone 2 - Global Qualification */}
          <div className="mb-8 md:mb-12">
            <div className="flex flex-row items-center gap-2 md:gap-4 mb-4 md:mb-6">
              <div>
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma/launch.webp?tr=w-48,h-48"
                  alt="Milestone Icon"
                  width={48}
                  height={48}
                  className="w-8 h-8 md:w-12 md:h-12 md:mb-1 mb-1"
                  loading="lazy"
                />
              </div>
              <div className="">
                <h2 className="text-[#3399FF] text-base md:text-2xl font-bold leading-[1] md:leading-[1]">
                  Milestone 2
                </h2>
                <h3 className="text-[#061A2F] text-sm md:text-xl font-semibold ">
                  Global Qualification - U.S. CPA / CMA
                </h3>
              </div>
            </div>

            {/* Instructor Section - Desktop */}
            <div className="hidden md:block mb-6">
              <div className="rounded-3xl p-4 border-2 border-[#3399FF]">
                <div className="flex items-center gap-4 mb-6">
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma/varun-badge.webp?tr=w-83,h-120,q-80"
                    alt="Varun Jain - World's Favourite CPA/CMA Instructor"
                    width={83}
                    height={120}
                    sizes="83px"
                    className=" h-auto"
                    loading="lazy"
                  />
                  <h4 className="text-[#3399FF] text-lg md:text-[22px] font-bold">
                    Learn from the World's Favourite CPA/CMA Instructor - Varun
                    Jain
                  </h4>
                </div>

                {/* CPA Timeline */}
                <div className="mb-6">
                  <div
                    className="mx-auto px-2 py-2"
                    style={{
                      borderRadius: "16px",
                      border: "1px solid #39F",
                      background:
                        "linear-gradient(0deg, rgba(51,153,255,0.10) 0%, rgba(51,153,255,0.10) 100%), rgba(255,255,255,0.04)",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <div className="flex flex-col justify-center items-center gap-4">
                      <h5 className="text-center text-black font-bold text-base md:text-lg">
                        CPA - Only 4 Exams & 12 Months Timeline
                      </h5>

                      <div className="flex flex-row space-x-16 items-start justify-between w-full flex-wrap">
                        <div className="flex flex-row items-start gap-3">
                          <Image
                            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-one.webp"
                            alt="1"
                            width={32}
                            height={32}
                            className="w-8 h-8 flex-shrink-0"
                            loading="lazy"
                          />
                          <p className="text-sm md:text-base text-black font-medium">
                            Financial Accounting & Reporting
                          </p>
                        </div>

                        <div className="flex flex-row items-start gap-3">
                          <Image
                            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-two.webp"
                            alt="2"
                            width={32}
                            height={32}
                            className="w-8 h-8 flex-shrink-0"
                            loading="lazy"
                          />
                          <p className="text-sm md:text-base text-black font-medium">
                            Auditing and Attestation
                          </p>
                        </div>

                        <div className="flex flex-row items-start gap-3">
                          <Image
                            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-three.webp"
                            alt="3"
                            width={32}
                            height={32}
                            className="w-8 h-8 flex-shrink-0"
                            loading="lazy"
                          />
                          <p className="text-sm md:text-base text-black font-medium">
                            Regulation
                          </p>
                        </div>

                        <div className="flex flex-row items-start gap-3">
                          <Image
                            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-four.webp"
                            alt="4"
                            width={32}
                            height={32}
                            className="w-8 h-8 flex-shrink-0"
                            loading="lazy"
                          />
                          <p className="text-sm md:text-base text-black font-medium">
                            Business Analysis & Reporting
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CMA Timeline */}
                <div>
                  <div
                    className=" mx-auto p-6"
                    style={{
                      borderRadius: "16px",
                      border: "1px solid #39F",
                      background:
                        "linear-gradient(0deg, rgba(51,153,255,0.10) 0%, rgba(51,153,255,0.10) 100%), rgba(255,255,255,0.04)",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <div className="flex flex-col justify-center items-center gap-4">
                      <h5 className="text-center text-black font-bold text-base md:text-lg">
                        CMA - Only 2 Exams & 8 Months Timeline
                      </h5>

                      <div className="flex flex-row gap-6 items-start justify-center w-full">
                        <div className="flex flex-row items-start gap-3">
                          <Image
                            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-one.webp"
                            alt="1"
                            width={32}
                            height={32}
                            className="w-8 h-8 flex-shrink-0"
                            loading="lazy"
                          />
                          <p className="text-sm md:text-base text-black font-medium">
                            Part 1 - Financial Planning, Performance & Analytics
                          </p>
                        </div>

                        <div className="flex flex-row items-start gap-3">
                          <Image
                            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-two.webp"
                            alt="2"
                            width={32}
                            height={32}
                            className="w-8 h-8 flex-shrink-0"
                            loading="lazy"
                          />
                          <p className="text-sm md:text-base text-black font-medium">
                            Part 2 - Strategic Financial Management
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Instructor Section - Mobile */}
            <div className="md:hidden mb-6">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-4 border-2 border-[#3399FF]">
                <div className="flex flex-row items-center gap-3 mb-4">
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma/varun-badge.webp?tr=w-75,h-75,q-80"
                    alt="Varun Jain - World's Favourite CPA/CMA Instructor"
                    width={75}
                    height={75}
                    sizes="75px"
                    className=" w-16 h-16"
                    loading="lazy"
                  />
                  <h4 className="text-[#3399FF] text-lg font-bold text-left leading-[1.1]">
                    Learn from the World's Favourite CPA/CMA Instructor - Varun
                    Jain
                  </h4>
                </div>

                {/* CPA Timeline Mobile */}
                <div className="mb-4">
                  <div
                    className="p-4"
                    style={{
                      borderRadius: "16px",
                      border: "1px solid #39F",
                      background:
                        "linear-gradient(0deg, rgba(51,153,255,0.10) 0%, rgba(51,153,255,0.10) 100%), rgba(255,255,255,0.04)",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <h5 className="text-center text-black font-bold text-sm mb-3">
                      CPA - Only 4 Exams & 12 Months Timeline
                    </h5>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-row items-start gap-2">
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-one.webp"
                          alt="1"
                          width={24}
                          height={24}
                          className="w-6 h-6 flex-shrink-0"
                          loading="lazy"
                        />
                        <p className="text-[10px] text-black font-medium">
                          Financial Accounting & Reporting
                        </p>
                      </div>
                      <div className="flex flex-row items-start gap-2">
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-two.webp"
                          alt="2"
                          width={24}
                          height={24}
                          className="w-6 h-6 flex-shrink-0"
                          loading="lazy"
                        />
                        <p className="text-[10px] text-black font-medium">
                          Auditing and Attestation
                        </p>
                      </div>
                      <div className="flex flex-row items-start gap-2">
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-three.webp"
                          alt="3"
                          width={24}
                          height={24}
                          className="w-6 h-6 flex-shrink-0"
                          loading="lazy"
                        />
                        <p className="text-[10px] text-black font-medium">
                          Regulation
                        </p>
                      </div>
                      <div className="flex flex-row items-start gap-2">
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-four.webp"
                          alt="4"
                          width={24}
                          height={24}
                          className="w-6 h-6 flex-shrink-0"
                          loading="lazy"
                        />
                        <p className="text-[10px] text-black font-medium">
                          Business Analysis & Reporting
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CMA Timeline Mobile */}
                <div>
                  <div
                    className="p-4"
                    style={{
                      borderRadius: "16px",
                      border: "1px solid #39F",
                      background:
                        "linear-gradient(0deg, rgba(51,153,255,0.10) 0%, rgba(51,153,255,0.10) 100%), rgba(255,255,255,0.04)",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <h5 className="text-center text-black font-bold text-sm mb-3">
                      CMA - Only 2 Exams & 8 Months Timeline
                    </h5>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-row items-start gap-2">
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-one.webp"
                          alt="1"
                          width={24}
                          height={24}
                          className="w-6 h-6 flex-shrink-0"
                          loading="lazy"
                        />
                        <p className="text-[10px] text-black font-medium">
                          Part 1 - Financial Planning, Performance & Analytics
                        </p>
                      </div>
                      <div className="flex flex-row items-start gap-2">
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-two.webp"
                          alt="2"
                          width={24}
                          height={24}
                          className="w-6 h-6 flex-shrink-0"
                          loading="lazy"
                        />
                        <p className="text-[10px] text-black font-medium">
                          Part 2 - Strategic Financial Management
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Alumni Badge */}
            <div
              className="py-4 px-6 text-center"
              style={{
                borderRadius: "8px",
                border: "1px solid #39F",
                background:
                  "linear-gradient(0deg, #FFB801 0%, #FFB801 100%), #FFF",
                backdropFilter: "blur(4px)",
              }}
            >
              <p className="text-black text-sm md:text-xl font-bold">
                80%+ CPAs in India are Miles Alumni
              </p>
            </div>
          </div>

          {/* Milestone 3 - Jobs */}
          <div>
            <div className="flex flex-col items-start">
              <div>
                <div className="flex flex-row items-center gap-2 md:gap-4 mb-4 md:mb-5">
                  <div>
                    <Image
                      src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-cma/launch.webp?tr=w-48,h-48"
                      alt="Milestone Icon"
                      width={48}
                      height={48}
                      className="w-8 h-8 md:w-12 md:h-12 md:mb-1 mb-1"
                      loading="lazy"
                    />
                  </div>
                  <div className="">
                    <h2 className="text-[#3399FF] text-base md:text-2xl font-bold leading-[1] md:leading-[1]">
                      Milestone 3
                    </h2>
                    <h3 className="text-[#061A2F] text-sm md:text-xl font-semibold ">
                      Jobs in India, U.S. & globally
                    </h3>
                  </div>
                </div>
              </div>
              <div>
                <ul className="list-disc list-outside pl-6 text-left text-black text-sm font-medium space-y-1 relative z-10">
                  <li>Work in India's top finance/ accounting roles</li>
                  <li>
                    Or move to the U.S. for 3 years via Miles' STEM Pathway
                  </li>
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 md:gap-7 w-full mx-auto pt-6">
              {cardsData.map((card, i) => (
                <SalaryCard key={i} {...card} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {isFormModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-auto mx-auto border border-white p-4 w-full max-w-[430px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleFormModelClose}
              className="text-white text-2xl absolute right-3 top-2 cursor-pointer"
            >
              ✕
            </button>
            <EnqueryForm
              course="CPA/CMA"
              courseId={1}
              formHeading={FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN}
              formType="page"
              isAStepperForm
              subCategory={FinanceAndAccountingSubcategory.CPA}
              submitButtonName="Request a call back"
              successMessage={SuccessMessageType.ACCOUNTING}
              verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
              darkBackground={true}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Milestone;
